import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, gql, useReactiveVar } from '@apollo/client';
import { take, drop } from 'lodash';
import {
  Pagination,
  Slider,
  Toggle,
  Dropdown,
  TextField,
  Grid,
} from '@statsbomb/kitbag-components';
import { mf_Leagues, mf_Seasons } from '../../../apollo';
import useQueryString from '../../../utils/hooks/useQueryString';
import {
  API_PLAYER_AGGREGATE_MODES,
  API_PLAYER_GROUP_BY,
} from '../../../utils/constants/api';
import {
  PLAYER_AGGREGATE_OPTIONS,
  PLAYER_GROUP_BY_OPTIONS,
  PLAYERS_PER_PAGE,
  PLAYER_STATS_PAGINATION_OPTIONS,
  PLAYER_GROUP_BY_HEADERS_CONFIG,
  PERMANENT_HEADERS_CONFIG,
  PLAYER_GROUP_BY_STICKY_COLUMNS_WIDTH,
  CATEGORY_GENERAL_POSITION_MAP,
  SPECIAL_TEAMS_POSITION_MAP,
  NO_TEAMS_OPTION,
  DEFAULT_SORT_BY,
  DEFAULT_HIGHLIGHTED_COLUMN,
  PHYSICAL_METRICS_MAP,
  SPECIAL_TEAMS_CATEGORY,
  PLAYS_SLIDER_FALLBACK_CONFIG,
} from './PlayerStats.constants';
import { ALIGNMENT } from '../../../utils/constants/alignment';
import { SORT_DIRECTIONS } from '../../../utils/constants/sortDirections';
import TabbedTable from '../../../components/Tables/TabbedTable/TabbedTable';
import { getPlayersStats } from './getPlayersStats';
import {
  getStatHeaders,
  formatStatValues,
  orderStats,
  getDownloadColumns,
} from '../../../utils/helpers/stats.dataManipulation';
import Tile from '../../../components/Tile/Tile';
import {
  addPlayerGroupColumns,
  getInitialMinPlays,
} from './PlayerStats.dataManipulation';
import useTypingState from '../../../utils/hooks/useTypingState';
import {
  useGetPlayerStatDefinitions,
  useGetPlayerStatsTeams,
  useGetPlaysSliderConfig,
} from './PlayerStats.hooks';
import { useIsNcaa } from '../../../utils/hooks/useIsNcaa';
import { useGetPlayerStatsCategoryDefinitions } from '../../../utils/hooks/useGetPlayerStatCategories';
import PositionFilter from '../../../components/PositionFilter/PositionFilter';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { KitbagPageGridHolder } from '../../../components/Layout/Layout.styles';
import { useExportSeasonInfo } from '../../../utils/hooks/useExportSeasonInfo';
import { PAGE_HEADER_DISPLAY } from '../../../components/PageHeader/PageHeader.constants';
import { PlayerStatsPagination } from './PlayerStats.styles';
import DownloadCSVButton from '../../../components/buttons/DownloadCSV';

const { ASCENDING, DESCENDING } = SORT_DIRECTIONS;
const { TEAM, PLAYER } = API_PLAYER_GROUP_BY;

function PlayerStats() {
  const leagueId = useReactiveVar(mf_Leagues);
  const seasonId = useReactiveVar(mf_Seasons);
  const isNcaa = useIsNcaa();
  const [aggMode, setAggMode] = useQueryString(
    'agg',
    PLAYER_AGGREGATE_OPTIONS[1].value,
  );
  const { seasonName } = useExportSeasonInfo({
    asObject: true,
  });
  const [groupByModes, setGroupByModes] = useState([]);
  const [selectedCategory, setSelectedCategory] = useQueryString(
    'category',
    '',
  );
  const [selectedSubcategory, setSelectedSubcategory] = useQueryString(
    'subcategory',
    '',
  );
  const [onlyTransferPortal, setOnlyTransferPortal] = useQueryString(
    'portal',
    false,
  );
  // minPlays starts empty and is filled in once the play distribution resolves,
  // since the slider's range depends on how far into the season we are
  const [minPlays, setMinPlays] = useQueryString('snaps', null);
  const [selectedTeam, setSelectedTeam] = useQueryString(
    'team',
    NO_TEAMS_OPTION.value,
  );
  const [minimumSnaps, setMinimumSnaps] = useState(minPlays);
  // only a slider interaction counts as a deliberate choice. the effect below
  // seeds ?snaps= with a default of its own, so the presence of the param
  // cannot stand in for user intent - after a reload the seeded default would
  // look exactly like a value the user had picked.
  const hasUserSetMinPlays = useRef(false);
  // whatever ?snaps= carried at mount, honoured once so a shared link opens on
  // the value it was shared with
  const linkedMinPlays = useRef(minPlays);
  const hasResolvedSliderConfig = useRef(false);
  // guards the player stats query until the playsSliderConfig effect has settled minPlays
  const [settledSliderConfig, setSettledSliderConfig] = useState(null);
  // guards the player stats query until the roster-position effect has run
  const hasAppliedRosterPositions = useRef(false);
  // holds the last successful result so the table doesn't flash empty during transitions
  const lastPlayerStatsRef = useRef(null);
  const [isTyping, setIsTyping, searchValue, setSearchValue] = useTypingState();
  const [sortDirection, setSortDirection] = useState(DESCENDING);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);
  const [highlightedColumn, setHighlightedColumn] = useState(
    DEFAULT_HIGHLIGHTED_COLUMN,
  );
  const [activePage, setActivePage] = useQueryString('page', 1);
  const [pageSize, setPageSize] = useState(PLAYERS_PER_PAGE);
  const [selectedRosterPositions, setSelectedRosterPositions] = useState([]);

  const { categoryDefinitions, isLoading: isLoadingCategoryDefinitions } =
    useGetPlayerStatsCategoryDefinitions();
  const { teamsLoading, teamsData } = useGetPlayerStatsTeams();
  const { playsSliderConfig, isLoadingPlaysSliderConfig } =
    useGetPlaysSliderConfig();

  // get all unique categories
  const categories =
    categoryDefinitions?.map((category) => category.name) || [];

  const selectedCategoryDefinition = categoryDefinitions?.find(
    (category) => category.name === selectedCategory,
  );

  // get all subcategories of the selected category
  const subcategories = selectedCategoryDefinition?.subcategories.map(
    (subcategory) => subcategory.name,
  );

  // get an ordered list of stats of the selected category/subcategory combination
  const orderedSubcategoryStats =
    selectedCategoryDefinition?.subcategories.find(
      (subcategory) => subcategory.name === selectedSubcategory,
    )?.stats;

  const isRateMode = aggMode === API_PLAYER_AGGREGATE_MODES.RATE;
  const { statDefinitions, isLoadingStatDefinitions } =
    useGetPlayerStatDefinitions(orderedSubcategoryStats, isRateMode);

  // set a default category if none is selected yet
  useEffect(() => {
    if (!selectedCategory && categories.length) {
      setSelectedCategory(categories[0]);
    }
  }, [isLoadingCategoryDefinitions]);

  const applyRosterPosition = (positions) => {
    hasAppliedRosterPositions.current = true;
    setSelectedRosterPositions(positions);
    setActivePage(1);
  };

  // set a default roster position filter based on selected category
  // in the case of special teams, default roster position is based on selected subcategory
  const isSpecialTeams = selectedCategory === SPECIAL_TEAMS_CATEGORY;
  const isPhysical = selectedCategory === 'Physical';
  useEffect(() => {
    if (selectedCategory && selectedSubcategory) {
      if (
        isSpecialTeams &&
        Object.keys(SPECIAL_TEAMS_POSITION_MAP).includes(selectedSubcategory)
      ) {
        const subcatDefaultRosterPositions =
          SPECIAL_TEAMS_POSITION_MAP[selectedSubcategory];
        applyRosterPosition(subcatDefaultRosterPositions);
      } else if (
        isPhysical &&
        Object.keys(PHYSICAL_METRICS_MAP).includes(selectedSubcategory)
      ) {
        const subcatDefaultRosterPositions =
          PHYSICAL_METRICS_MAP[selectedSubcategory];
        applyRosterPosition(subcatDefaultRosterPositions);
      } else {
        const catDefaultRosterPositions =
          CATEGORY_GENERAL_POSITION_MAP[selectedCategory];
        applyRosterPosition(catDefaultRosterPositions);
      }
    }
  }, [selectedCategory, selectedSubcategory]);

  // special teams players take very few snaps, so that category is always
  // allowed down to 0 regardless of the range the season progress suggests
  const sliderConfig = playsSliderConfig || PLAYS_SLIDER_FALLBACK_CONFIG;
  const effectiveSliderConfig = isSpecialTeams
    ? { ...sliderConfig, min: 0, defaultValue: 0 }
    : sliderConfig;

  // minimumSnaps is empty until the range resolves, but the slider is controlled
  const sliderValue = minimumSnaps ?? effectiveSliderConfig.min;

  // apply the resolved slider range to the current value. a value the user
  // chose is clamped into range; anything else takes the range's default, so
  // switching to a season at a different stage re-defaults rather than
  // clamping a default this effect applied earlier.
  // config objects are module constants, so this only re-runs when the range
  // actually changes.
  useEffect(() => {
    if (!playsSliderConfig) {
      return;
    }
    // a value the user chose is clamped into range. a ?snaps= they arrived with
    // counts the first time the range resolves, but not afterwards: by then the
    // param holds whatever default this effect last seeded.
    let chosenMinPlays = null;
    if (hasUserSetMinPlays.current) {
      chosenMinPlays = minPlays;
    } else if (!hasResolvedSliderConfig.current) {
      chosenMinPlays = linkedMinPlays.current;
    }
    hasResolvedSliderConfig.current = true;

    const resolvedMinPlays = getInitialMinPlays(
      effectiveSliderConfig,
      chosenMinPlays,
    );

    // raising the minimum can shrink the results past the page being viewed,
    // which would otherwise render an empty table. every other path that
    // changes the minimum resets pagination too
    if (minPlays !== null && resolvedMinPlays > minPlays) {
      setActivePage(1);
    }

    // the user did not ask for this value, so it should not cost them a
    // history entry to press back through
    setMinPlays(resolvedMinPlays, { shouldReplace: true });
    setMinimumSnaps(resolvedMinPlays);
    setSettledSliderConfig(playsSliderConfig);
  }, [playsSliderConfig]);

  // set default subcategory once the category has been set
  useEffect(() => {
    if (subcategories?.length) {
      setSelectedSubcategory(subcategories[0]);
    }
  }, [selectedCategory]);

  const handleSearch = (e) => {
    if (activePage !== 1) {
      setActivePage(1);
    }
    setSearchValue(e.target.value);
    setIsTyping(true);
  };

  const groupByMode = [PLAYER, TEAM, ...groupByModes.map((mode) => mode.value)];
  const teamIds = selectedTeam === NO_TEAMS_OPTION.value ? [] : [selectedTeam];

  const sharedVariables = {
    competitionId: leagueId,
    seasonId,
    groupByMode,
    playerName: searchValue,
    minPlays,
    // apply transfer portal flag only when on NCAA
    ncaaTransferPortal: isNcaa && onlyTransferPortal,
    rosterPositions: selectedRosterPositions,
    teamIds,
  };

  const playerStatsQuery = getPlayersStats(statDefinitions);

  // query to get all data (no server-side sorting or pagination)
  const { data: playerStatsData, loading: isLoadingPlayerStats } = useQuery(
    gql(playerStatsQuery),
    {
      // skip until both the playsSliderConfig effect has settled minPlays for
      // the current config AND the roster-position effect has applied positions.
      // settledSliderConfig prevents firing with a stale minPlays when a season
      // change produces a new config (isMinPlaysSettled only catches out-of-range
      // values and misses in-range values that the effect would still re-default).
      skip:
        !statDefinitions?.length ||
        isTyping ||
        playsSliderConfig !== settledSliderConfig ||
        !hasAppliedRosterPositions.current,
      variables: {
        ...sharedVariables,
      },
      context: {
        headers: {
          'Accept-Version': 'player_stats_v2',
        },
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  if (playerStatsData) lastPlayerStatsRef.current = playerStatsData;
  const playerStats = addPlayerGroupColumns(
    (playerStatsData ?? lastPlayerStatsRef.current)?.playerStats,
  );

  // insert additional group by column if selected
  const groupByColumns = groupByModes.length
    ? groupByModes.map((mode) => PLAYER_GROUP_BY_HEADERS_CONFIG[mode.value])
    : [];

  const headers = [
    ...PERMANENT_HEADERS_CONFIG,
    ...groupByColumns,
    ...getStatHeaders(statDefinitions),
  ];

  const headerIds = headers.map((header) => header.id);

  const formattedStats = formatStatValues(
    playerStats,
    headerIds,
    statDefinitions,
    aggMode,
  );

  // Add metadata (groups, teamName) to each stat row for later lookup
  const statsWithMetadata = formattedStats?.map((stat, index) => ({
    ...stat,
    _groups: playerStats[index]?.groups,
    _teamName: playerStats[index]?.teamName,
  }));

  const orderedStats = orderStats(statsWithMetadata, headerIds);

  // Client-side sorting function with stable sort (tie-breaker by player name)
  const sortStats = (stats, sortByColumn, direction) => {
    if (!stats || !sortByColumn) return stats;

    return [...stats].sort((a, b) => {
      const aValue = a[sortByColumn];
      const bValue = b[sortByColumn];

      // Handle null/undefined values
      if (
        (aValue === null || aValue === undefined) &&
        (bValue === null || bValue === undefined)
      )
        return 0;
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Convert to numbers for numeric comparison
      const aNum = Number(aValue);
      const bNum = Number(bValue);

      let result = 0;

      // If both are valid numbers, compare numerically
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
        result = direction === DESCENDING ? bNum - aNum : aNum - bNum;
      } else {
        // Otherwise compare as strings
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();

        if (direction === DESCENDING) {
          result = bStr.localeCompare(aStr);
        } else {
          result = aStr.localeCompare(bStr);
        }
      }

      // if values are equal, sort by player name (ascending) for stable sort
      if (result === 0) {
        const aName = String(a.playerName || '').toLowerCase();
        const bName = String(b.playerName || '').toLowerCase();
        return aName.localeCompare(bName);
      }

      return result;
    });
  };

  // Apply client-side sorting
  const sortedStats = sortStats(orderedStats, sortBy, sortDirection);

  // Get total count for pagination
  const totalCount = sortedStats?.length || 0;

  // Apply client-side pagination
  const playersSkipped = (activePage - 1) * pageSize;
  const paginatedStats = useMemo(
    () => take(drop(sortedStats, playersSkipped), pageSize),
    [sortedStats, playersSkipped, pageSize],
  );

  // Prepare CSV download data from current query results (exclude metadata fields)
  const csvData =
    sortedStats?.map((stat) => {
      const { _groups, _teamName, ...statData } = stat;
      return Object.keys(statData).reduce(
        (acc, key) => ({
          ...acc,
          [key]: statData[key],
        }),
        {},
      );
    }) || [];

  const getOverviewLink = (groups) => {
    const teamId = groups.find(({ key }) => key === TEAM).value;
    const playerId = groups.find(({ key }) => key === PLAYER).value;
    return `/player/overview/${leagueId}/${seasonId}/${teamId}/${playerId}`;
  };

  // convert the stats to objects so we can pass extra context like "title" and "link"
  const enrichedStats = paginatedStats?.map((stat) => {
    const { _groups, _teamName, ...statData } = stat;
    return Object.keys(statData).reduce(
      (acc, key) => ({
        ...acc,
        [key]: {
          label: statData[key],
          title: key === 'teamShortName' ? _teamName : '',
          link: key === 'playerName' && _groups ? getOverviewLink(_groups) : '',
        },
      }),
      {},
    );
  });

  const stickyColumns = [
    { index: 1, width: 2 },
    { index: 2, width: 8.125 },
    { index: 3, width: 3.75 },
    { index: 4, width: 3.75 },
    { index: 5, width: 3.75 },
    ...groupByModes.map((mode, index) => ({
      index: index + 1 + PERMANENT_HEADERS_CONFIG.length,
      width: PLAYER_GROUP_BY_STICKY_COLUMNS_WIDTH[mode.value],
    })),
  ];

  const playerStatsConfig = categories?.map((category) => ({
    id: category,
    label: category,
    tableConfig: {
      headers,
      rows: enrichedStats,
      alignment: (index) =>
        stickyColumns.length > index && index !== 0 && index !== 4
          ? ALIGNMENT.LEFT
          : ALIGNMENT.RIGHT,
      stickyColumns,
    },
  }));

  const downloadColumns = getDownloadColumns(headers);

  const handleSort = (statName) => {
    if (statName === sortBy) {
      setSortDirection(sortDirection === ASCENDING ? DESCENDING : ASCENDING);
    } else {
      setSortDirection(DESCENDING);
      setSortBy(statName);
      setHighlightedColumn(headerIds.indexOf(statName) + 1);
    }
    setActivePage(1);
  };

  const handleTabChange = (e, tab, isSubTab) => {
    const setTab = isSubTab ? setSelectedSubcategory : setSelectedCategory;
    setTab(tab?.label);
    setSortBy(DEFAULT_SORT_BY);
    setActivePage(1);
    setHighlightedColumn(DEFAULT_HIGHLIGHTED_COLUMN);

    if (!isSubTab) {
      setSelectedSubcategory('');
    }

    // set minimum snaps filter to 0 when the Special Teams category is selected.
    // while the range is still loading sliderConfig is only a fallback, so leave
    // minPlays alone and let the effect above apply the real default instead.
    if (
      !isSubTab &&
      tab.label !== selectedCategory &&
      !isLoadingPlaysSliderConfig
    ) {
      const nextMinPlays =
        tab.label === SPECIAL_TEAMS_CATEGORY ? 0 : sliderConfig.defaultValue;
      // this value comes from the category, not from the user, so a later
      // change of season should re-default it rather than clamp it
      hasUserSetMinPlays.current = false;
      setMinPlays(nextMinPlays);
      setMinimumSnaps(nextMinPlays);
    }
  };

  const handleAggModeChange = (selectedOption) => {
    setAggMode(selectedOption.value);
    setSortBy(DEFAULT_SORT_BY);
    setHighlightedColumn(DEFAULT_HIGHLIGHTED_COLUMN);
    setActivePage(1);
  };

  const isLoading =
    isLoadingPlayerStats ||
    isLoadingStatDefinitions ||
    isLoadingCategoryDefinitions ||
    isLoadingPlaysSliderConfig ||
    isTyping;

  return (
    <KitbagPageGridHolder>
      <Grid container={false} page>
        <Grid item xs={10}>
          <PageHeader
            href="/league/stats/players/:leagues/:seasons/"
            rootPage="League"
            activePage="Player Stats"
            titleType={PAGE_HEADER_DISPLAY.CUSTOM}
          >
            <div className="buttons">
              <TextField
                id="player-stats-search-filter"
                labelPosition="none"
                value={searchValue}
                onChange={handleSearch}
                adornmentIcon="Search"
                adornmentOnClick={() => {}}
                placeholder="Search players"
                size="small"
              />
              <DownloadCSVButton
                data={csvData}
                headers={downloadColumns}
                fileName={`Player Stats ${seasonName}.csv`}
              />
            </div>
          </PageHeader>
          <Tile border="0" margin="0.5rem 0">
            <Tile.Body $padding="0" $gap="0">
              {selectedSubcategory && (
                <TabbedTable
                  tableTabsConfig={playerStatsConfig}
                  isLoading={isLoading}
                  onTabChange={(e, tab) => handleTabChange(e, tab, false)}
                  subTabs={subcategories}
                  selectedSubTab={selectedSubcategory}
                  handleSort={handleSort}
                  onSubTabChange={(e, tab) => handleTabChange(e, tab, true)}
                  sortDirection={sortDirection}
                  sortBy={sortBy || statDefinitions?.[0]?.name}
                  withBorder
                  isHeadSticky
                  highlightedColumn={highlightedColumn}
                  nonTableHeight="calc(100vh - 21rem)"
                />
              )}
              {enrichedStats && totalCount > 0 && (
                <PlayerStatsPagination>
                  <Pagination
                    activePage={activePage}
                    onChange={({ pageSelected }) => setActivePage(pageSelected)}
                    itemsPerPage={pageSize}
                    dataLength={totalCount}
                    hasEdgePageButtons
                    labelText={`of ${totalCount} player${
                      totalCount !== 1 ? 's' : ''
                    }`}
                    size="small"
                  />
                  <Dropdown
                    id="player-stats-pagination-options"
                    isSearchable={false}
                    menuAlignment="right"
                    menuPlacement="top"
                    menuPosition="absolute"
                    onChange={({ value }) => {
                      setPageSize(value);
                      setActivePage(1);
                    }}
                    options={PLAYER_STATS_PAGINATION_OPTIONS}
                    value={PLAYER_STATS_PAGINATION_OPTIONS.find(
                      (option) => option.value === pageSize,
                    )}
                    labelPosition="none"
                    variant="tertiary"
                    size="small"
                  />
                  per page
                </PlayerStatsPagination>
              )}
            </Tile.Body>
          </Tile>
        </Grid>
        <Grid item xs={2}>
          <Tile border="0" margin="0 0 0.5rem 0">
            <Tile.Body>
              <Dropdown
                options={teamsData}
                label="Filter by team"
                isLoading={teamsLoading}
                value={teamsData?.find((t) => t.value === selectedTeam)}
                id="player-stats-team-filter"
                onChange={(selectedOption) => {
                  setSelectedTeam(selectedOption.value);
                  setActivePage(1);
                }}
                isDisabled={teamsLoading}
              />
              <Dropdown
                options={PLAYER_AGGREGATE_OPTIONS}
                label="View values aggregated"
                value={PLAYER_AGGREGATE_OPTIONS.find(
                  (f) => f.value === aggMode,
                )}
                id="player-stats-aggregate-filter"
                onChange={handleAggModeChange}
              />
              <Dropdown
                options={PLAYER_GROUP_BY_OPTIONS}
                label="Break player stats down by"
                value={groupByModes}
                id="player-stats-group-by-filter"
                onChange={(selectedOptions) => {
                  setGroupByModes(selectedOptions);
                  setActivePage(1);
                }}
                isMulti
              />
              {isNcaa && (
                <Toggle
                  onChange={() => {
                    setOnlyTransferPortal(!onlyTransferPortal);
                    setActivePage(1);
                  }}
                  checked={onlyTransferPortal}
                  label="NCAA Transfer Portal"
                  id="player-stats-transfer-portal-filter"
                />
              )}
            </Tile.Body>
          </Tile>
          <Tile border="0" margin="0 0 0.5rem 0">
            <Tile.Body>
              <div>
                <Slider
                  id="minimum-snaps-filter"
                  labels={[
                    {
                      hasNotch: true,
                      label: `${effectiveSliderConfig.min}`,
                      position: effectiveSliderConfig.min,
                    },
                    {
                      hasNotch: true,
                      label: `${effectiveSliderConfig.max}`,
                      position: effectiveSliderConfig.max,
                    },
                  ]}
                  max={effectiveSliderConfig.max}
                  min={effectiveSliderConfig.min}
                  step={effectiveSliderConfig.step}
                  isDisabled={isLoadingPlaysSliderConfig}
                  label="Minimum Total Plays"
                  value={sliderValue}
                  valueLabel={`≥ ${sliderValue}`}
                  onChange={(e, val) => {
                    setMinimumSnaps(val);
                  }}
                  onChangeComplete={(e, val) => {
                    hasUserSetMinPlays.current = true;
                    setMinPlays(val);
                    setActivePage(1);
                  }}
                  variant="greater-than"
                />
              </div>
            </Tile.Body>
          </Tile>
          <Tile border="0" margin="0">
            <Tile.Body>
              <PositionFilter
                selectedPositions={selectedRosterPositions}
                setSelectedPositions={applyRosterPosition}
                isRosterPositions
              />
            </Tile.Body>
          </Tile>
        </Grid>
      </Grid>
    </KitbagPageGridHolder>
  );
}

export default PlayerStats;
